// Constants for the battle system
export const DEFAULT_RECHARGE_TIME = 2000; // 2 seconds default recharge
export const DEFAULT_GRAVITY = 0.5;  // Default gravity value
export const JUMP_FORCE = -15;
export const MOVE_SPEED = 5;
export const PLATFORM_HEIGHT = 20;
export const ATTACK_RANGE = 150;

// --- Phase 2 mechanics constants ---
// Weight tuning for the jump-stomp damage calculation. A character at
// BASE_WEIGHT deals JUMP_DAMAGE_AMOUNT × 1.0 on a stomp; heavier
// characters scale linearly up to JUMP_WEIGHT_MULT_CAP.
export const BASE_WEIGHT = 20;
export const JUMP_WEIGHT_MULT_CAP = 3.0;
// How much incoming damage is multiplied by while the F key is held
// AND the glass shield is intact. 0.2 = 80% reduction.
export const BLOCK_DAMAGE_MULTIPLIER = 0.2;
// Glass shield: an F-press raises the shield; it absorbs SHIELD_MAX_HITS
// before it shatters and stops working. Resets on each new wave / round
// (single-player) or on respawn (multiplayer with lives > 0).
export const SHIELD_MAX_HITS = 2;
// Cooldown (ms) between consecutive shield activations. F is press-to-
// activate now (not hold), so without this two presses in a row would
// be a free reblock; 5s is enough that you really commit to each raise.
export const SHIELD_COOLDOWN_MS = 5000;
// How long a raised shield stays up before auto-dropping. Caps free
// uptime so the player can't just leave F pressed once and tank the
// whole round; they have to time the raise to incoming damage.
export const SHIELD_UP_DURATION_MS = 2500;
// Half-arc of the shield "window", in radians. Attacks whose incoming
// vector falls inside the arc centered on the shield angle are blocked;
// hits from outside (i.e. behind / from the side) get full damage.
// Math.PI/2 = ±90°, so the shield blocks a 180° front hemisphere.
export const SHIELD_ARC_HALF = Math.PI / 2;
// Passive health regeneration for every living character (players + AI).
// At 8 HP/sec, a 100-HP character heals back to full in ~12s — fast
// enough that a clean disengage actually recovers you before the next
// exchange, slow enough that you still can't tank a fight indefinitely.
// Tiered: level-1 starters regen the slowest so tier-1 vs tier-1 fights
// don't drag into a stalemate where both characters out-heal incoming
// damage; level-3 forms regen at the full rate.
export const HEALTH_REGEN_PER_SEC = 8;
export const HEALTH_REGEN_BY_LEVEL = { 1: 2, 2: 5, 3: 8 };
export function regenRateForLevel(level) {
    const v = HEALTH_REGEN_BY_LEVEL[level];
    return (typeof v === 'number') ? v : HEALTH_REGEN_PER_SEC;
}
// Minimum gap between regen ticks (ms). The actual heal amount scales
// with the time elapsed, so a long gap just heals more in one step —
// this just keeps us from spamming CHARACTER_HEALTH_UPDATED events.
// Also drives the cadence of the "+N" floating heal text.
export const REGEN_TICK_INTERVAL_MS = 400;
// Street-Fighter hit-stop freeze durations (ms). Trimmed: still sells
// the impact but doesn't slow the pace between hits.
export const HITSTOP_MS_NORMAL = 30;
export const HITSTOP_MS_BIG = 65;
// Stomp bounce: the player's upward velocity after landing on an enemy,
// expressed as a multiple of JUMP_FORCE. 0.3 (old) → tiny hop;
// 0.85 (new) → almost a fresh jump.
export const STOMP_BOUNCE_FACTOR = 0.85;

// --- Per-Snordeluxe upgradeable stats ---
// Each inventory item carries three upgrade stats: stamina (running
// speed), power (attack damage), and weight (stomp damage). Each upgrade
// click costs STAT_UPGRADE_MANA_COST (1) mana and adds STAT_UPGRADE_STEP
// (25) to the chosen stat, capped at STAT_MAX (100). When all three
// stats reach a tier threshold the snordeluxe auto-evolves to its next
// form (chain defined in snordeluxeAssets.js). Tiers: 25, 50, 100.
export const STAT_MAX = 100;
export const STAT_UPGRADE_STEP = 25;
export const STAT_UPGRADE_MANA_COST = 1;
// Tier thresholds the user listed: 25 = first evolution, 50 = second,
// 100 = max. Each entry is the minimum value EACH of the three stats
// must reach to trigger that tier's evolution.
export const STAT_TIER_THRESHOLDS = [25, 50, 100];
// Stamina → movement speed multiplier. At 100 stamina, characters
// move 50% faster (1.5×). Power → attack damage multiplier: at 100
// power, +100% damage (2×). Weight stat → additive into the existing
// character.weight (which already drives stomp damage in physics).
export const STAMINA_SPEED_BONUS_AT_MAX = 0.5;
export const POWER_DAMAGE_BONUS_AT_MAX = 1.0;

// Critical hit roll on every damage-dealing impact. 15% chance to roll a
// crit, which multiplies the final post-mitigation damage by
// CRIT_MULTIPLIER. The hit event carries isCritical:true so the renderer
// can swap the floating number color and the SFX subscriber can play
// critical_hit.mp3 instead of the normal hit.mp3.
export const CRIT_CHANCE = 0.15;
export const CRIT_MULTIPLIER = 1.75;

// --- Host-selectable game modes ---
// Lifesteal: every kill grants the killer +1 livesRemaining. No cap —
// snowballing is the point.
// Fat Mode: every kill grows the killer by FAT_GROW_PER_KILL on width
// and height, up to FAT_MAX_SCALE × the starting size. Only the hitbox
// changes — damage dealt is NOT affected (projectile damage is fixed
// per-attack; stomp damage scales with weight, not size). The trade-off
// is purely "bigger target": easier for others to hit, easier for you
// to land stomps. Death resets stacks (see handleCharacterRespawn).
export const FAT_GROW_PER_KILL = 0.12;
export const FAT_MAX_SCALE = 2.0;
// Glass Cannon: both damage dealt and damage taken multiplied by this.
// Same multiplier on both sides so the mode is faster — not asymmetric.
export const GLASS_CANNON_MULTIPLIER = 2.0;
// Low Gravity: scales the theme's default gravity. Floaty jumps,
// longer hang time; aim becomes the dominant skill.
export const LOW_GRAVITY_MULTIPLIER = 0.5;

// Hold-to-charge attack tuning. Holding an attack key (1/2/3) accrues
// "charge" up to CHARGE_MAX_MS. The release fires an attack scaled
// linearly between 1x (no charge) and CHARGE_MAX_DAMAGE_MULT damage at
// full charge — then adds CHARGE_COOLDOWN_PENALTY_PER_SEC_MS extra
// cooldown for every second held. A tap of <= CHARGE_MIN_MS is treated
// as zero-charge so quick fire doesn't get any penalty.
export const CHARGE_MAX_MS = 4000;
export const CHARGE_MIN_MS = 180;
export const CHARGE_MAX_DAMAGE_MULT = 3.0;
// Cooldown penalty added per second of hold. Trimmed to a token amount
// because the user kept perceiving partial charges as "didn't fire" —
// even at 600ms/sec the followup tap landed inside the penalty window.
// Charging is supposed to be a damage choice, not a cooldown trap.
export const CHARGE_COOLDOWN_PENALTY_PER_SEC_MS = 100;

// Attack types and properties
export const ATTACK_TYPES = {
  bite: {
    damage: 10,
    cooldown: 1000,
    label: 'Bite',
    description: 'Quick attack with moderate damage'
  },
  slam: {
    damage: 15,
    cooldown: 2000,
    label: 'Body Slam',
    description: 'Powerful attack with longer cooldown'
  },
  rest: {
    damage: -20, // Negative damage means healing
    cooldown: 8000,
    label: 'Rest',
    description: 'Restore health but vulnerable during cooldown'
  },
  hyperBeam: {
    damage: 30,
    cooldown: 5000,
    label: 'Hyper Beam',
    description: 'Extremely powerful long-range attack with long cooldown'
  }
};

// Theme definitions
export const THEMES = [
  'volcano',
  'ice',
  'jungle',
  'space',
  'candy',
  'underwater'
];

// Theme-specific effects
export const THEME_EFFECTS = {
  volcano: {
    gravity: DEFAULT_GRAVITY,
    damageOverTime: 2, // Damage when in lava
    particleColors: ['#ff6b00', '#ff4500', '#ff8c00']
  },
  ice: {
    gravity: DEFAULT_GRAVITY,
    frictionFactor: 0.98, // Slippery surface
    particleColors: ['#a5f2f3', '#ffffff', '#d1f3ff']
  },
  jungle: {
    gravity: DEFAULT_GRAVITY * 1.1, // Slightly higher gravity
    healOverTime: 0.5, // Health regeneration
    particleColors: ['#7cff00', '#00aa11', '#90ee90']
  },
  space: {
    gravity: DEFAULT_GRAVITY * 0.6, // Lower gravity
    jumpModifier: 1.3, // Higher jumps
    particleColors: ['#ffffff', '#aaaaff', '#ffaaff']
  },
  candy: {
    gravity: DEFAULT_GRAVITY,
    speedModifier: 1.2, // Faster movement
    particleColors: ['#ff69b4', '#ff1493', '#00ffff']
  },
  underwater: {
    gravity: DEFAULT_GRAVITY * 0.5, // Lower gravity
    dragFactor: 0.9, // Movement slows down
    particleColors: ['#00aaff', '#0088cc', '#00ddff']
  }
};

// Game state constants
export const BATTLE_STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  IN_PROGRESS: 'in_progress',
  PAUSED: 'paused',
  VICTORY: 'victory',
  DEFEAT: 'defeat',
  DRAW: 'draw'
};

// Reward definitions
export const REWARD_TYPES = {
  COINS: 'coins',
  XP: 'xp',
  ITEM: 'item'
};

// Difficulty settings
export const DIFFICULTY_LEVELS = {
  EASY: {
    enemyDamageMultiplier: 0.8,
    enemyHealthMultiplier: 0.9,
    rewardMultiplier: 0.9
  },
  NORMAL: {
    enemyDamageMultiplier: 1.0,
    enemyHealthMultiplier: 1.0,
    rewardMultiplier: 1.0
  },
  HARD: {
    enemyDamageMultiplier: 1.2,
    enemyHealthMultiplier: 1.3,
    rewardMultiplier: 1.4
  },
  EXTREME: {
    enemyDamageMultiplier: 1.5,
    enemyHealthMultiplier: 2.0,
    rewardMultiplier: 2.0
  }
};

// AI difficulty settings
export const AI_SETTINGS = {
  EASY: {
    reactionTime: 800, // ms
    attackFrequency: 0.3, // probability to attack when in range
    moveRandomness: 0.5 // randomness in movement decisions
  },
  NORMAL: {
    reactionTime: 500,
    attackFrequency: 0.5,
    moveRandomness: 0.3
  },
  HARD: {
    reactionTime: 300,
    attackFrequency: 0.7,
    moveRandomness: 0.2
  },
  EXTREME: {
    reactionTime: 150,
    attackFrequency: 0.9,
    moveRandomness: 0.1
  }
};

// New consolidated theme configuration
export const THEME_CONFIG = {
  volcano: {
    backgroundImage: 'assets/images/themes/volcano_bg.png',
    gradientColors: ['#1a0500', '#4d1500'],
    particleColors: ['#ff6b00', '#ff4500', '#ff8c00'],
    groundLevelPercent: 0.75,
    hideMainGroundPlatform: true
  },
  ice: {
    backgroundImage: 'assets/images/themes/ice_bg.png', // Placeholder path
    gradientColors: ['#e6f2ff', '#99ccff'],
    particleColors: ['#a5f2f3', '#ffffff', '#d1f3ff'],
    groundLevelPercent: 0.80, 
    hideMainGroundPlatform: true
  },
  jungle: {
    backgroundImage: 'assets/images/themes/jungle_bg.png', // Placeholder path
    gradientColors: ['#004d00', '#006600'],
    particleColors: ['#7cff00', '#00aa11', '#90ee90'],
    groundLevelPercent: 0.70,
    hideMainGroundPlatform: true
  },
  space: {
    backgroundImage: 'assets/images/themes/space_bg.png', // Placeholder path
    gradientColors: ['#000033', '#000066'],
    particleColors: ['#ffffff', '#aaaaff', '#ffaaff'],
    groundLevelPercent: 0.75, 
    hideMainGroundPlatform: true // Example: Space might not have a "main ground"
  },
  candy: {
    backgroundImage: 'assets/images/themes/candy_bg.png', // Placeholder path
    gradientColors: ['#ffccee', '#ffb3e6'],
    particleColors: ['#ff69b4', '#ff1493', '#00ffff'],
    groundLevelPercent: 0.78,
    hideMainGroundPlatform: true
  },
  underwater: {
    backgroundImage: 'assets/images/themes/underwater_bg.png', // Placeholder path
    gradientColors: ['#006699', '#0099cc'],
    particleColors: ['#00aaff', '#0088cc', '#00ddff'],
    groundLevelPercent: 0.85, 
    hideMainGroundPlatform: true
  },
  desert: { 
    backgroundImage: 'assets/images/themes/desert_bg.png',
    gradientColors: ['#f4a460', '#cd853f'], 
    particleColors: ['#deb887', '#ffe4b5', '#sandybrown'],
    groundLevelPercent: 0.75,
    hideMainGroundPlatform: true
  },
  default: { 
    backgroundImage: 'assets/images/themes/desert_bg.png',
    gradientColors: ['#87CEEB', '#4682B4'],
    particleColors: [`rgba(255, 255, 255, ${0.3 + Math.random() * 0.7})`],
    groundLevelPercent: 0.85,
    hideMainGroundPlatform: true
  }
};

// POWERUP SETTINGS
export const POWERUP_SETTINGS = {
  HEALTH_MUSHROOM: {
    healthAmount: 25, // Amount of health restored
    spawnFrequency: 0.05, // Chance to spawn per second (approx) - INCREASED FOR TESTING
    duration: 15000, // Milliseconds before it disappears
    bobSpeed: 0.15, // Pixels per frame for bobbing - SLOWED DOWN
    bobHeight: 2.5, // Max height of bobbing - LIMITED
    sprite: { // Simple representation for drawing
      capColor: 'red',
      spotColor: 'white',
      stemColor: 'beige',
      width: 20, // pixels
      height: 20 // pixels
    }
  }
}; 